"use strict";
/**
 * Sanitizer - PII redaction utility for error messages and logs
 * Redacts: passwords, credit cards, bank accounts, emails, UUIDs, SSNs
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.Sanitizer = void 0;
/**
 * Sanitizer - Utility class for redacting sensitive data
 */
class Sanitizer {
    /**
     * Recursively sanitize a value (string, object, array, or primitive)
     * Redacts sensitive patterns while preserving structure
     */
    static sanitize(input) {
        if (typeof input === 'string') {
            return Sanitizer.sanitizeString(input);
        }
        if (Array.isArray(input)) {
            return input.map((item) => Sanitizer.sanitize(item));
        }
        if (typeof input === 'object' && input !== null) {
            return Sanitizer.sanitizeObject(input);
        }
        // Return primitives as-is (boolean, number, null, undefined, etc.)
        return input;
    }
    /**
     * Sanitize a string by redacting sensitive patterns
     */
    static sanitizeString(str) {
        return (str
            // Redact passwords: password, pwd, secret, token, apiKey, etc.
            .replace(/("password"\s*:\s*)"[^"]*"/gi, '$1"***"')
            .replace(/("passwd"\s*:\s*)"[^"]*"/gi, '$1"***"')
            .replace(/("pwd"\s*:\s*)"[^"]*"/gi, '$1"***"')
            .replace(/("secret"\s*:\s*)"[^"]*"/gi, '$1"***"')
            .replace(/("token"\s*:\s*)"[^"]*"/gi, '$1"***"')
            .replace(/("apikey"\s*:\s*)"[^"]*"/gi, '$1"***"')
            .replace(/("api_key"\s*:\s*)"[^"]*"/gi, '$1"***"')
            // Redact credit cards: 16 digits with optional spaces/dashes
            .replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, '****-****-****-****')
            // Redact bank accounts: 8-10 digits
            .replace(/\b\d{8,10}\b/g, '****')
            // Redact email addresses (preserve format: first 2 chars + ***@***)
            .replace(/\b([a-z0-9]{2})[a-z0-9._%+-]*@[a-z0-9.-]+\.[a-z]{2,}\b/gi, '$1***@***')
            // Redact ownerId and other UUIDs (UUID v4 pattern)
            .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '****-****-****-****-****')
            // Redact SSN (XXX-XX-XXXX pattern)
            .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '***-**-****')
            // Redact credit card numbers (16 digits no spacing)
            .replace(/\b\d{16}\b/g, '****-****-****-****')
            // Redact Clerk user IDs and organization IDs
            .replace(/\b(user|org)_[a-zA-Z0-9]{20,}\b/g, '$1_****')
            // Redact AWS access keys and secret keys
            .replace(/AKIA[0-9A-Z]{16}/g, 'AKIA****')
            .replace(/(?:aws_)?secret_access_key[\s]*[:=][\s]*[A-Za-z0-9/+=]{40}/g, '***'));
    }
    /**
     * Sanitize an object recursively
     * Skips sensitive keys entirely, sanitizes string values
     */
    static sanitizeObject(obj) {
        if (obj === null) {
            return null;
        }
        if (!isPlainObject(obj)) {
            return obj;
        }
        const sanitized = {};
        for (const [key, value] of Object.entries(obj)) {
            // Skip sensitive fields entirely (replace with ***)
            if (Sanitizer.isSensitiveKey(key)) {
                sanitized[key] = '***';
                continue;
            }
            // Recursively sanitize the value
            sanitized[key] = Sanitizer.sanitize(value);
        }
        return sanitized;
    }
    /**
     * Check if a key name indicates sensitive data
     */
    static isSensitiveKey(key) {
        const sensitivePatterns = [
            /password/i,
            /passwd/i,
            /pwd/i,
            /secret/i,
            /token/i,
            /apikey/i,
            /api_key/i,
            /authorization/i,
            /auth/i,
            /creditcard/i,
            /credit_card/i,
            /cardnumber/i,
            /ssn/i,
            /social_security/i,
            /bankaccount/i,
            /bank_account/i,
            /routing/i,
            /privatekey/i,
            /private_key/i,
            /accesskey/i,
            /access_key/i,
            /secretkey/i,
            /secret_key/i,
        ];
        return sensitivePatterns.some((pattern) => pattern.test(key));
    }
}
exports.Sanitizer = Sanitizer;
/**
 * Type guard to check if a value is a plain object
 */
function isPlainObject(value) {
    return (typeof value === 'object' &&
        value !== null &&
        value.constructor === Object &&
        Object.getPrototypeOf(value) === Object.prototype);
}
